import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { ExerciseSearchDropdown } from "../components/shared";

// ── Monkey anonymization ───────────────────────────────────────────────────────
const SPECIES = [
  "Gorilla","Mandrill","Baboon","Macaque","Gibbon","Marmoset","Tamarin",
  "Capuchin","Proboscis","Colobus","Langur","Bonobo","Siamang","Orangutan",
  "Gelada","Uakari","Vervet","Howler","Spider","Squirrel",
];
function strHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function getAlias(name) {
  const h = strHash(name);
  const species = SPECIES[h % SPECIES.length];
  const num = String((h % 9000) + 1000);
  return `${species}#${num}`;
}

// ── Podium (top 3) ────────────────────────────────────────────────────────────
function Podium({ users, currentUser, getDisplay, getInitials: getInit }) {
  if (!users || users.length === 0) return null;

  const getInitials = getInit || (u => getAlias(u.user_name).slice(0, 2).toUpperCase());

  const slots = [
    { rank: 2, user: users[1] },
    { rank: 1, user: users[0] },
    { rank: 3, user: users[2] },
  ];
  const rankColor = r => r === 1 ? "#b9c8de" : r === 2 ? "#c2c7d0" : "#e1c19d";

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
      gap: 8, alignItems: "flex-end", marginBottom: 36,
    }}>
      {slots.map(({ rank, user: u }) => {
        if (!u) return <div key={rank}/>;
        const isFirst = rank === 1;
        const isMe    = u.user_name === currentUser;
        const rc      = rankColor(rank);
        const alias   = isMe ? "YOU" : getAlias(u.user_name);
        const display = getDisplay ? getDisplay(u) : "";

        return (
          <div key={rank} style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            ...(isFirst ? { transform: "scale(1.1) translateY(-10px)" } : {}),
          }}>
            <div style={{ position: "relative", marginBottom: isFirst ? 16 : 10 }}>
              <div style={{
                width: isFirst ? 72 : 60, height: isFirst ? 72 : 60,
                borderRadius: 8, padding: isFirst ? 3 : 2,
                background: isFirst
                  ? "linear-gradient(135deg, #b9c8de, #94a3b8, #1c1b1b)"
                  : `linear-gradient(180deg, ${rc}40, transparent)`,
                boxShadow: isFirst ? "0 0 28px -8px rgba(185,200,222,0.4)" : "none",
              }}>
                <div style={{
                  width: "100%", height: "100%", borderRadius: 6,
                  background: "var(--bg-highest)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: isMe ? (isFirst ? 14 : 11) : (isFirst ? 18 : 14), fontWeight: 900,
                  color: isMe ? "#b9c8de" : rc,
                }}>
                  {isMe ? "YOU" : alias.slice(0, 2).toUpperCase()}
                </div>
              </div>
              {isFirst ? (
                <div style={{
                  position: "absolute", bottom: -10, left: "50%", transform: "translateX(-50%)",
                  background: "#b9c8de", color: "#233143",
                  padding: "2px 10px", borderRadius: 2,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 900, fontSize: 11, whiteSpace: "nowrap",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                }}>RANK 01</div>
              ) : (
                <div style={{
                  position: "absolute", bottom: -8, right: -8,
                  background: "var(--bg-highest)", border: "1px solid rgba(68,71,76,0.25)",
                  padding: "2px 6px", borderRadius: 4,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700, fontSize: 10, color: rc,
                }}>#{rank}</div>
              )}
            </div>
            <span style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
              color: isMe ? "#b9c8de" : "var(--text)", textTransform: "uppercase",
              maxWidth: isFirst ? 80 : 68,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              textAlign: "center",
            }}>{alias}</span>
            <span style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 10, color: rc, marginTop: 1,
              fontWeight: isFirst ? 700 : 400,
            }}>{display}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── User row in the ranked list ───────────────────────────────────────────────
function UserRow({ rank, name, sub, value, valueLabel, highlight, isCurrentUser }) {
  const alias    = isCurrentUser ? "YOU" : getAlias(name);
  const initials = alias.slice(0, 2).toUpperCase();

  if (isCurrentUser) {
    return (
      <div style={{
        position: "relative", margin: "0 -8px", padding: "14px 22px",
        borderTop: "1px solid rgba(185,200,222,0.1)",
        borderBottom: "1px solid rgba(185,200,222,0.1)",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(185,200,222,0.04)" }}/>
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 900, color: "#b9c8de", width: 36, flexShrink: 0 }}>{rank + 1}</span>
            <div style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0, background: "var(--primary-dim)", padding: 1 }}>
              <div style={{ width: "100%", height: "100%", borderRadius: 5, background: "var(--bg-highest)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 900, color: "#b9c8de" }}>YOU</div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 700, color: "#b9c8de", textTransform: "uppercase", letterSpacing: "0.05em" }}>YOU</span>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#b9c8de", display: "inline-block", animation: "pulse 2s infinite" }}/>
              </div>
              {sub && <div style={{ fontSize: 10, color: "rgba(185,200,222,0.6)", marginTop: 1 }}>{sub}</div>}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 900, color: "#b9c8de" }}>{value}</div>
            {valueLabel && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, color: "rgba(185,200,222,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 1 }}>{valueLabel}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--bg-low)", padding: "12px 14px", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontFamily: "'Space Grotesk', sans-serif", width: 16, fontSize: 12, fontWeight: 700, color: "#8e9197", flexShrink: 0, textAlign: "center" }}>{rank + 1}</span>
        <div style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0, background: "var(--bg-highest)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 900, color: "#8e9197" }}>{initials}</div>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>{alias}</div>
          {sub && <div style={{ fontSize: 10, color: "#8e9197", marginTop: 1 }}>{sub}</div>}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 700, color: highlight ? "var(--primary)" : "var(--text)" }}>{value}</div>
        {valueLabel && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 1 }}>{valueLabel}</div>}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function LeaderboardView({ user, units = "lbs" }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState("volume");
  const [exerciseFilter, setExerciseFilter] = useState(null);
  const [muscleFilter, setMuscleFilter]     = useState(null);

  useEffect(() => {
    apiFetch("/leaderboard").then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div className="label-caps">LOADING LEADERBOARD...</div>
    </div>
  );
  if (!data) return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div className="label-caps" style={{ color: "var(--error)" }}>FAILED TO LOAD</div>
    </div>
  );

  const TABS = [
    { id: "volume", label: "VOLUME" },
    { id: "orm",    label: "1RM"    },
    { id: "muscle", label: "MUSCLE" },
  ];

  const exerciseNames = [...new Set(data.exercisePrs.map(r => r.exercise_name))].sort();
  const muscleNames   = [...new Set(data.muscleRanks.map(r => r.muscle))].sort();
  const cvt = w => units === "kg" ? Math.round(w * 0.453592 * 10) / 10 : w;
  const fmtVol = v => {
    const n = units === "kg" ? Math.round(Number(v||0)*0.453592) : Number(v||0);
    if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`;
    if (n >= 1000)    return `${(n/1000).toFixed(0)}K`;
    return String(n);
  };

  // ── Shared ranked list renderer ──────────────────────────────────────────────
  const RankedList = ({ title, badge, rows }) => (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em" }}>{title}</h2>
        {badge && <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#8e9197", letterSpacing: "0.12em", background: "var(--bg-high)", padding: "3px 8px", borderRadius: 4 }}>{badge}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, i) => (
          <UserRow key={i} rank={i + 3} name={r.name}
            sub={r.sub} value={r.value} valueLabel={r.valueLabel}
            highlight isCurrentUser={r.name === user} />
        ))}
      </div>
    </>
  );

  const renderVolume = () => {
    if (exerciseFilter) {
      const perEx = {};
      data.exercisePrs.filter(r => r.exercise_name === exerciseFilter).forEach(r => {
        if (!perEx[r.user_name] || r.best_weight > perEx[r.user_name].best_weight) perEx[r.user_name] = r;
      });
      const sorted = Object.values(perEx).sort((a, b) => b.best_weight - a.best_weight);
      if (sorted.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO DATA FOR THIS EXERCISE</div></div>;
      return (
        <>
          <Podium users={sorted.slice(0, 3)} currentUser={user} getDisplay={u => `${cvt(u.best_weight)} ${units.toUpperCase()}`} />
          <RankedList title={exerciseFilter} badge="BEST WEIGHT"
            rows={sorted.slice(3).map(r => ({ name: r.user_name, value: `${cvt(r.best_weight)} ${units.toUpperCase()}`, valueLabel: "BEST" }))} />
        </>
      );
    }

    if (data.volumeRanks.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO DATA YET</div></div>;
    return (
      <>
        <Podium users={data.volumeRanks.slice(0, 3)} currentUser={user} getDisplay={u => `${fmtVol(u.total_volume)} ${units.toUpperCase()}`} />
        <RankedList title="All-Time Volume Leaders" badge={`LIFETIME ${units.toUpperCase()}`}
          rows={data.volumeRanks.slice(3).map(r => ({ name: r.user_name, sub: `${r.total_sets} sets`, value: Number(r.total_volume||0).toLocaleString(), valueLabel: `${units.toUpperCase()} TOTAL` }))} />
      </>
    );
  };

  const renderOrm = () => {
    if (exerciseFilter) {
      const perUser = {};
      data.exercisePrs.filter(r => r.exercise_name === exerciseFilter).forEach(r => {
        if (!perUser[r.user_name] || r.best_weight > perUser[r.user_name].best_weight) perUser[r.user_name] = r;
      });
      const sorted = Object.values(perUser).sort((a, b) => b.best_weight - a.best_weight);
      if (sorted.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO DATA FOR THIS EXERCISE</div></div>;
      return (
        <>
          <Podium users={sorted.slice(0, 3)} currentUser={user} getDisplay={u => `${cvt(u.best_weight)} ${units.toUpperCase()}`} />
          <RankedList title={exerciseFilter} badge={units.toUpperCase()}
            rows={sorted.slice(3).map(r => ({ name: r.user_name, value: `${cvt(r.best_weight)} ${units.toUpperCase()}`, valueLabel: "1RM" }))} />
        </>
      );
    }

    // No filter: top users by their all-time best weight across any exercise
    const bestPerUser = {};
    data.exercisePrs.forEach(r => {
      if (!bestPerUser[r.user_name] || r.best_weight > bestPerUser[r.user_name].best_weight)
        bestPerUser[r.user_name] = r;
    });
    const sorted = Object.values(bestPerUser).sort((a, b) => b.best_weight - a.best_weight);
    if (sorted.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO EXERCISE PRS YET</div></div>;
    return (
      <>
        <Podium users={sorted.slice(0, 3)} currentUser={user} getDisplay={u => `${cvt(u.best_weight)} ${units.toUpperCase()}`} />
        <RankedList title="All-Time 1RM Leaders" badge={`BEST ${units.toUpperCase()}`}
          rows={sorted.slice(3).map(r => ({ name: r.user_name, sub: r.exercise_name, value: `${cvt(r.best_weight)} ${units.toUpperCase()}`, valueLabel: "1RM" }))} />
      </>
    );
  };

  const renderMuscle = () => {
    if (muscleFilter) {
      const records = data.muscleRanks.filter(r => r.muscle === muscleFilter)
        .sort((a, b) => Number(b.muscle_sets||0) - Number(a.muscle_sets||0));
      if (records.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO DATA FOR THIS MUSCLE</div></div>;
      return (
        <>
          <Podium users={records.slice(0, 3)} currentUser={user} getDisplay={u => `${u.muscle_sets} SETS`} />
          <RankedList title={muscleFilter} badge="SETS"
            rows={records.slice(3).map(r => ({
              name: r.user_name,
              sub: `${units === "kg" ? Math.round(Number(r.muscle_volume||0)*0.453592).toLocaleString() : Number(r.muscle_volume||0).toLocaleString()} ${units} vol`,
              value: r.muscle_sets, valueLabel: "SETS",
            }))} />
        </>
      );
    }

    // No filter: top users by total sets across all muscles
    const totalPerUser = {};
    data.muscleRanks.forEach(r => {
      if (!totalPerUser[r.user_name]) totalPerUser[r.user_name] = { user_name: r.user_name, total_sets: 0, total_volume: 0 };
      totalPerUser[r.user_name].total_sets   += Number(r.muscle_sets || 0);
      totalPerUser[r.user_name].total_volume += Number(r.muscle_volume || 0);
    });
    const sorted = Object.values(totalPerUser).sort((a, b) => b.total_sets - a.total_sets);
    if (sorted.length === 0) return <div style={{ textAlign: "center", padding: "32px 0" }}><div className="label-caps">NO MUSCLE DATA YET</div></div>;
    return (
      <>
        <Podium users={sorted.slice(0, 3)} currentUser={user} getDisplay={u => `${u.total_sets} SETS`} />
        <RankedList title="All-Time Muscle Leaders" badge="TOTAL SETS"
          rows={sorted.slice(3).map(r => ({ name: r.user_name, value: r.total_sets, valueLabel: "SETS" }))} />
      </>
    );
  };

  return (
    <div className="fade-in">


      {/* ── TABS ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setExerciseFilter(null); setMuscleFilter(null); }} style={{
            flex: 1, padding: "9px 8px", borderRadius: 8,
            background: tab === t.id ? "#1e293b" : "var(--bg-low)",
            border: `1px solid ${tab === t.id ? "rgba(185,200,222,0.25)" : "rgba(68,71,76,0.3)"}`,
            color: tab === t.id ? "#f1f5f9" : "#64748b",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
            cursor: "pointer", textTransform: "uppercase",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── FILTER (context-aware) ── */}
      <div style={{ marginBottom: 24 }}>
        {tab !== "muscle" ? (
          <ExerciseSearchDropdown
            items={exerciseNames}
            value={exerciseFilter}
            onChange={setExerciseFilter}
          />
        ) : (
          <ExerciseSearchDropdown
            items={muscleNames}
            value={muscleFilter}
            onChange={setMuscleFilter}
            placeholder="Search muscle..."
          />
        )}
      </div>

      {/* ── CONTENT ── */}
      {tab === "volume" && renderVolume()}
      {tab === "orm"    && renderOrm()}
      {tab === "muscle" && renderMuscle()}

    </div>
  );
}
